import { EASE, html, reduceMotion } from './env';
import { setDrawer } from './contact-drawer';
import { setMenu } from './menu';
import { lenis } from './scroll';

/*
  Transition de page : un rideau orange (logo imprimé dessus, statique : seul le
  masque bouge) monte et couvre l'écran, puis la page suivante se charge.

  Le site est multipage : on pose un drapeau `curtain` dans sessionStorage avant
  de partir. Sur la page suivante, le script inline de layouts/base.twig le lit
  et ajoute `html.is-entering`, donc le rideau couvre l'écran dès le premier
  rendu (voir « Rideau de transition » dans src/css/components/_index.css).
  On joue alors la sortie du rideau vers le haut.
*/

const STORAGE_KEY = 'curtain';
const CURVE = '14vh';
const HIDDEN_BELOW = `inset(100% 0 0 0 round 50% 50% 0 0 / ${CURVE} ${CURVE} 0 0)`;
const COVERING = 'inset(0 0 0 0 round 0)';
const HIDDEN_ABOVE = `inset(0 0 100% 0 round 0 0 50% 50% / 0 0 ${CURVE} ${CURVE})`;

let leaving = false;

function storage(action: 'set' | 'remove') {
  try {
    if (action === 'set') sessionStorage.setItem(STORAGE_KEY, '1');
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // stockage indisponible (navigation privée stricte…) : pas de rideau à l'arrivée
  }
}

function getCurtain(): HTMLElement {
  const existing = document.querySelector<HTMLElement>('[data-curtain]');
  if (existing) return existing;
  // Repli si le gabarit ne le fournit pas : un rideau sans logo.
  const curtain = document.createElement('div');
  curtain.className = 'curtain';
  curtain.dataset.curtain = '';
  curtain.setAttribute('aria-hidden', 'true');
  document.body.append(curtain);
  return curtain;
}

/** Rideau masqué, état de repos. */
function hide(curtain: HTMLElement) {
  curtain.style.visibility = 'hidden';
  curtain.style.pointerEvents = 'none';
  html.classList.remove('is-entering');
  for (const animation of curtain.getAnimations()) animation.cancel();
  lenis?.start();
}

async function leave(curtain: HTMLElement) {
  lenis?.stop();
  curtain.style.visibility = 'visible';
  curtain.style.pointerEvents = 'all'; // bloque les clics pendant qu'il couvre
  await curtain.animate([{ clipPath: HIDDEN_BELOW }, { clipPath: COVERING }], {
    duration: 700,
    easing: EASE,
    fill: 'forwards',
  }).finished;
}

async function enter(curtain: HTMLElement, ready: Promise<unknown>) {
  lenis?.stop();
  curtain.style.visibility = 'visible';
  curtain.style.pointerEvents = 'all';
  await ready;
  await new Promise((resolve) => {
    window.setTimeout(resolve, 150); // courte pause, écran entièrement couvert
  });
  document.querySelector('main')?.animate(
    [
      { opacity: 0, transform: 'translateY(2.5rem)' },
      { opacity: 1, transform: 'none' },
    ],
    {
      duration: 1000,
      delay: 150,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
      fill: 'backwards',
    },
  );
  await curtain.animate([{ clipPath: COVERING }, { clipPath: HIDDEN_ABOVE }], {
    duration: 800,
    easing: EASE,
    fill: 'forwards',
  }).finished;
  hide(curtain);
}

/** Lien vers une autre page du site, à ouvrir dans l'onglet courant ? */
function transitionUrl(event: MouseEvent): URL | null {
  if (event.defaultPrevented || event.button !== 0) return null;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;

  const link = (event.target as Element | null)?.closest?.<HTMLAnchorElement>('a[href]');
  if (!link || link.hasAttribute('download') || link.closest('[data-contact-open]')) return null;
  if (link.target && link.target !== '_self') return null;

  const url = new URL(link.href, location.href);
  // mailto:, tel:, autre domaine
  if (!/^https?:$/.test(url.protocol) || url.origin !== location.origin) return null;
  // ancre de la page courante (gérée par Lenis / le navigateur)
  const samePage = url.pathname === location.pathname && url.search === location.search;
  if (samePage && (url.hash || link.getAttribute('href')?.includes('#'))) return null;
  // fichier (PDF, image…) plutôt qu'une page
  if (/\.[a-z0-9]+$/i.test(url.pathname) && !/\.(?:html?|php)$/i.test(url.pathname)) return null;

  return url;
}

export function initCurtain(ready: Promise<unknown>) {
  const curtain = getCurtain();
  const entering = html.classList.contains('is-entering');
  storage('remove');

  if (entering) {
    void enter(curtain, ready);
  }

  // Retour arrière depuis le cache (bfcache) : la page revient avec le rideau
  // fermé par leave(), on le retire sans animation.
  addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    leaving = false;
    storage('remove');
    hide(curtain);
  });

  if (reduceMotion) return;

  document.addEventListener('click', (event) => {
    const url = transitionUrl(event);
    if (!url) return;
    event.preventDefault();
    if (leaving) return;
    leaving = true;
    setMenu(false, { restoreFocus: false });
    setDrawer(false, { restoreFocus: false });
    void leave(curtain).then(() => {
      storage('set');
      location.href = url.href;
    });
  });
}
